<template>
  <div class="app-col" :style="colStyle">
    <slot />
  </div>
</template>

<script setup lang="ts">
const COLS = 12;

const props = defineProps<{
  cols?: number;
}>();

const colStyle = computed(() => {
  if (props.cols && props.cols > 0 && props.cols <= COLS) {
    const percentage = (props.cols / COLS) * 100;
    return {
      flex: `0 0 ${percentage}%`,
      maxWidth: `${percentage}%`
    };
  }
  // Default behavior if cols is not specified or invalid
  // This will make columns grow and shrink, starting from a flex-basis of 0.
  // This is a common pattern for flex items that should distribute space equally
  // or based on content if they have content-based min-widths.
  return {
    flex: '1 1 0%'
  };
});
</script>

<style scoped lang="scss">
.app-col {
  box-sizing: border-box;
  padding-left: var(--space-3);
  padding-right: var(--space-3);

  &:first-child {
    padding-inline-start: 0;
  }
  &:last-child:not(:first-child) {
    padding-inline-end: 0;
  }
}

@media (min-width: 851px) {
  .app-col:only-child {
     // This rule is potentially problematic with the new `flex: '1 1 0%'` default.
     // If `cols` is not provided, `flex: '1 1 0%'` would make it take 100% width.
     // If you *do* want a single column without `cols` to be 50%, this rule is needed,
     // but it might conflict with other flex behaviors.
     // Let's keep it for now to match the original, but be aware of its interaction
     // with the default flex: '1 1 0%'. The flex: '1 1 0%' and flex-wrap might already
     // handle a single item taking full width correctly in most cases.
     max-width: 50%; // Apply this if it's the ONLY child and no `cols` is set.
                     // Note: This will be overridden by inline style if `cols` is set.
  }
}

@media (max-width: 850px) {
  .app-col {
    min-width: min(400px, 100%);
    width: 100%;
    max-width: 100%;
    flex-basis: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    padding: 0;
  }
  .app-col:not(:last-child) {
    padding-bottom: var(--space-6);
  }
}
</style>